import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Button, Stack } from '@mui/material';

import CardWithShadow from '@components/CardWithShadow/CardWithShadow';
import InquiryStatus from '@components/InquiryStatus/InquiryStatus';
import Loadable from '@components/Loadable/Loadable';
import { TInquiryStatus } from '@interfaces/inquiries';
import { ISupplementInquiry } from '@interfaces/supplements';
import { handleGetInquiryDetails } from '@store/features/inquiryDraft/inquiryDetails/actions';
import {
  inquiryDetailsErrorSelector,
  inquiryDetailsIsLoadingSelector,
  inquiryDetailsSelector,
} from '@store/features/inquiryDraft/inquiryDetails/selectors';
import useDispatch from '@store/hooks/useDispatch';

import SupplementInquiryCard from './SupplementInquiryCard/SupplementInquiryCard';
import { IInquirySupplementsCardProps } from './InquirySupplementsCard.interface';

import './style.scss';

const InquirySupplementsCard: FC<IInquirySupplementsCardProps> = ({
  onSelectSupplement,
  onRemoveSupplementFromInquiry,
  onDeleteInquiry,
  onSubmitInquiry,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const inquiry = useSelector(inquiryDetailsSelector);
  const isLoading = useSelector(inquiryDetailsIsLoadingSelector);
  const error = useSelector(inquiryDetailsErrorSelector);

  const isDraft = inquiry?.inquiryStatusId === TInquiryStatus.Draft;
  const supplementsTotal = inquiry?.supplementInquiriesDto.length || 0;

  const handleSelectSupplement = useCallback(
    (supplement: ISupplementInquiry) => {
      supplement.supplementId &&
        onSelectSupplement(
          null,
          {
            id: supplement.supplementId,
            name: supplement.name,
            isMedication: false,
          },
          null,
        );

      supplement.userEnteredSupplementId &&
        onSelectSupplement(null, null, supplement.userEnteredSupplementId);
    },
    [onSelectSupplement],
  );

  const handleRemoveInquiry = useCallback(() => {
    inquiry && onDeleteInquiry(inquiry.id);
  }, [onDeleteInquiry, inquiry]);

  const handleRetryFetchInquiryDetails = useCallback(() => {
    !!inquiry && dispatch(handleGetInquiryDetails({ id: inquiry.id }));
  }, [dispatch, inquiry]);

  return (
    <CardWithShadow className="inquiry-supplements-card" borderType="dashed">
      <Box className="inquiry-supplements-card__header">
        <h6>{t('dietarySupplementInquiry.emptyCardTitle')}</h6>

        {inquiry && <InquiryStatus value={inquiry.inquiryStatusId} />}
      </Box>

      <Box className="inquiry-supplements-card__divider" />

      <Loadable
        isLoading={isLoading}
        error={error && t('errors.inquiryDetailsError')}
        onRetry={handleRetryFetchInquiryDetails}
      >
        {inquiry && (
          <Box className="inquiry-supplements-card__new-inquiry-sub-header">
            <p className="caption">{inquiry.name}</p>
            {isDraft && (
              <p
                className="inquiry-supplements-card__remove-inquiry-action caption"
                onClick={handleRemoveInquiry}
              >
                {t('dietarySupplementInquiry.removeInquiry')}
              </p>
            )}
          </Box>
        )}

        {(!inquiry || !supplementsTotal) && (
          <Box className="inquiry-supplements-card__section">
            <Box className="inquiry-supplements-card__icon">
              <Box component="img" src="/images/img-clipboard.svg" />
            </Box>

            <Box className="inquiry-supplements-card__description">
              <p className="subtitle-big">{t('dietarySupplementInquiry.emptyCardNotification')}</p>
            </Box>
          </Box>
        )}

        <Stack spacing={2}>
          {inquiry?.supplementInquiriesDto.map(supplement => (
            <SupplementInquiryCard
              key={supplement.supplementInquiryId}
              data={supplement}
              isEditable={isDraft}
              isRemovable={supplementsTotal !== 1}
              onSelect={handleSelectSupplement}
              onRemove={onRemoveSupplementFromInquiry}
            />
          ))}
        </Stack>

        {!!supplementsTotal && (
          <Box className="inquiry-supplements-card__actions">
            <Button
              variant="contained"
              disabled={!isDraft}
              className="inquiry-supplements-card__action-submit"
              onClick={onSubmitInquiry}
            >
              <Box component="img" src="/images/img-submit.svg" />
              <p className="label">{t('dietarySupplementInquiry.submitSupplementInquiry')}</p>
            </Button>
          </Box>
        )}
      </Loadable>
    </CardWithShadow>
  );
};

export default InquirySupplementsCard;
